import events from 'events.js';
import http from 'http.js';
import ko from 'knockout';

const DESKTOP_TYPE_LIMIT = 10;
const MOBILE_TYPE_LIMIT = 5;

function setBackgroundColours() {
    for (const elem of Array.from(
        document.getElementsByClassName('dynamic'),
    )) {
        const colorBackground = elem.style.backgroundColor.match(
            /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
        );
        if (!colorBackground) {
            continue;
        }
        const colorRed = colorBackground[1];
        const colorGreen = colorBackground[2];
        const colorBlue = colorBackground[3];

        // hsp equation from http://alienryderflex.com/hsp.html
        const hsp = Math.sqrt(
            0.299 * (colorRed * colorRed) +
                0.587 * (colorGreen * colorGreen) +
                0.114 * (colorBlue * colorBlue),
        );

        if (hsp < 255 * 0.75) {
            elem.classList.add('inverted');
        } else {
            elem.classList.remove('inverted');
        }
    }
}

class SearchResult {
    constructor(mgr) {
        this.mgr = mgr;
        this.text = ko.observable('');
        this.type = ko.observable('');
        this.url = ko.observable('');
        this.selectable = ko.observable(true);
        this.prefillText = ko.pureComputed(() => this.text());
        this.template = ko.observable('search-result');
        this.action = this.action.bind(this);
    }

    load(data) {
        this.text(data.text);
        this.type(data.object_type);
        this.url(data.url);
    }

    // Performed when selected and user hits enter
    action() {
        window.open(this.url(), '_top');
    }
}

class SearchTitle {
    constructor(mgr, facet) {
        this.mgr = mgr;
        this.type = ko.observable('search-title');
        this.template = ko.observable(`search-title-${facet}`);
        this.selectable = ko.observable(false);
        this.facet = facet;
    }
}

class NoResults {
    constructor(mgr) {
        this.mgr = mgr;
        this.text = ko.observable(`No results for "${mgr.typedQuery()}"`);
        this.type = ko.observable('no-results');
        this.template = ko.observable('no-results-result');
        this.selectable = ko.observable(false);
    }
}

class MoreResults {
    constructor(mgr) {
        this.mgr = mgr;
        this.text = ko.observable(
            `Show more results for “${mgr.typedQuery()}”`,
        );
        this.type = ko.observable('more-results');
        this.template = ko.observable('more-results-result');
        this.selectable = ko.observable(true);

        this.prefillText = ko.pureComputed(function() {
            return mgr.typedQuery();
        });

        this.action = this.action.bind(this);
    }

    action() {
        const query = encodeURIComponent(this.mgr.typedQuery()).replace(
            /%20/g,
            '+',
        );
        if (this.mgr.isSearchResults()) {
            this.mgr.loadMore();
        } else {
            window.open(`${this.mgr.baseUrl}?q=${query}`, '_top');
        }
    }
}

class SearchManager {
    static SORT_RELEVANCY = 'relevancy';
    static SORT_ALPHABETICAL = 'alphabetical';

    constructor(typeLimit) {
        this.baseUrl = '/search/';
        this.searchUrl = '/api/search/v1/all/';
        this.query = ko.observable('').extend({
            'rateLimit': 200,
            'method': 'notifyWhenChangesStop',
        });
        this.typedQuery = ko.observable();
        this.autoSetQuery = false;
        this.results = ko.observableArray();
        this.selectedIndex = ko.observable(-1);
        this.searching = ko.observable(false);
        this.sortCriteria = ko.observable(SearchManager.SORT_RELEVANCY);
        this.filters = ko.observableArray();
        this.totalCount = ko.observable();
        this.isSearchResults = ko.observable(false);
        this.totals = {};
        this.elems = [];
        this.limit = typeLimit || 10;
        this.typeLimit = ko.observable(typeLimit);
        this.facets = [];
        this.sortOrder = ['factsheets', 'threads'];
        this.showMore = false;

        this.sortedResults = ko.pureComputed(() => {
            if (this.typeLimit()) {
                if (
                    !this.results().length ||
                    this.results()[0].type() === 'no-results'
                ) {
                    return this.results();
                }

                const results = [];
                const data = {};

                let calcLimit = Math.min(
                    this.typeLimit(),
                    this.results().length,
                );
                const calcFacets = this.facets.slice();
                while (calcLimit > 0) {
                    const toRemove = [];
                    const startingCalcLimit = calcLimit;
                    for (const facet of calcFacets) {
                        if (!data[facet.type]) {
                            data[facet.type] = {
                                count: facet.count,
                                length: 0,
                                results: [],
                            };
                        }
                        const index = calcFacets.indexOf(facet);
                        const length = calcFacets.length - index;
                        let amount = Math.ceil(calcLimit / length);
                        if (amount >= facet.count - data[facet.type].length) {
                            amount = facet.count - data[facet.type].length;
                            toRemove.push(facet);
                        }
                        data[facet.type].length += amount;
                        calcLimit -= amount;
                    }
                    for (const facet of toRemove) {
                        calcFacets.splice(calcFacets.indexOf(facet), 1);
                    }
                    // fail safe if calcLimit never reaches 0
                    if (startingCalcLimit === calcLimit) {
                        break;
                    }
                }

                let amountAdded = 0;
                for (const result of this.results()) {
                    const d = data[result.type()];
                    if (!d) {
                        continue;
                    }
                    if (d.results.length < d.length) {
                        d.results.push(result);
                        amountAdded++;
                    }
                    if (amountAdded >= this.typeLimit()) {
                        break;
                    }
                }

                for (const orderedFacet of this.sortOrder) {
                    for (const facet of this.facets) {
                        if (orderedFacet !== facet.type) {
                            continue;
                        }
                        if (!data[facet.type].length) {
                            continue;
                        }
                        const facetType = data[facet.type];
                        results.push(new SearchTitle(this, facet.type));
                        results.push(...facetType.results);
                    }
                }

                if (
                    this.results().length > this.typeLimit() ||
                    this.showMore
                ) {
                    results.push(new MoreResults(this));
                }

                return results;
            } else {
                if (this.sortCriteria() === this.SORT_RELEVANCY) {
                    return this.results();
                } else if (this.sortCriteria() === this.SORT_ALPHABETICAL) {
                    return this.results()
                        .slice(0)
                        .sort(function(a, b) {
                            if (a.sortText && !b.sortText) {
                                return -1;
                            } else if (b.sortText && !a.sortText) {
                                return 1;
                            }
                            if (
                                a.sortText().toLowerCase() >
                                b.sortText().toLowerCase()
                            ) {
                                return 1;
                            } else {
                                return -1;
                            }
                        });
                }
                return [];
            }
        });
        this.isHidden = ko.pureComputed(() =>
            this.sortedResults().length ? 'false' : 'true',
        );

        // Track what user actually typed as we may interfere with query text
        this.query.subscribe((val) => {
            if (this.autoSetQuery) {
                // If flagged not to update, clean up flag
                this.autoSetQuery = false;
            } else {
                this.typedQuery(val);
            }
        });

        this.selected = ko.pureComputed(() => {
            if (this.selectedIndex() > -1) {
                return this.sortedResults.peek()[this.selectedIndex()];
            }
        });

        // When selection changes, pre-fill text box(es) with result
        this.selected.subscribe((selection) => {
            if (!selection) {
                return;
            }
            const text = selection.prefillText();
            this.autoSetQuery = true; // flag to typedQuery sub not to update
            this.query(text);
            // If this begins with what user typed, then select text
            // following it, so user can keep typing onto their result
            const typedQuery = this.typedQuery();
            if (
                text.substring(0, typedQuery.length).toLowerCase() !==
                typedQuery.toLowerCase()
            ) {
                return;
            }
            for (const e of this.elems) {
                if (!e) {
                    break;
                }
                const elem = e.querySelector('input[name=q]');
                if (!elem || !elem.setSelectionRange) {
                    break;
                }
                // Since we don't get notified when value actually updates
                // and we want to select only once that happens, poll on the
                // value before selecting
                const interval = setInterval(function() {
                    if (elem.value === text) {
                        elem.setSelectionRange(typedQuery.length, text.length);
                        clearInterval(interval);
                    }
                }, 20);
            }
        });

        this.hasMore = ko.computed(
            () => this.sortedResults().length < this.totalCount(),
        );

        this.typedQuery.subscribe(() => this.search(null, true));

        // catch all clicks that haven't been cancelled from text input
        // & close dropdown
        // because this happens on html, any lower level click handlers happen
        // sooner
        // This means that actions are guaranteed to have executed before we
        // close the dropdown
        events.listen(document.body, 'click', (e) => {
            const elems = Array.from(
                document.getElementsByClassName('js-ignore-dropdown-close'),
            );
            for (const elem of elems) {
                if (elem.contains(e.target)) {
                    document.getElementsByClassName('js-query')[0].focus();
                    return;
                }
            }
            this.close();
        });

        events.listen(
            document.getElementsByClassName('js-query')[0],
            'focus',
            () => {
                if (this.typedQuery() && !this.sortedResults().length) {
                    this.search(null, true);
                }
            },
        );

        this.keydown = this.keydown.bind(this);
        this.select = this.select.bind(this);
    }

    loadMore() {
        const lastResult = this.results()[this.results().length - 1];
        if (lastResult.type() === 'more-results') {
            this.results.remove(lastResult);
        }
        this.search(null);
    }

    async search(q, clearResults) {
        // cancel previous request if still going
        if (this.searching()) {
            this.req.abort();
        }
        if (q) {
            this.query(q);
        }
        if (this.query().length < 3) {
            this.results([]);
            this.filters([]);
            this.searching(false);
            return;
        }
        if (this.isSearchResults()) {
            if (history.pushState) {
                const q = encodeURIComponent(this.query()).replace(
                    /%20/g,
                    '+',
                );
                history.pushState(
                    {'query': this.query()},
                    document.title,
                    `${location.origin}${location.pathname}?q=${q}`,
                );
            }
        }
        this.searching(true);

        const data = {
            health_fund: document.body.dataset.healthFund,
            limit: this.typeLimit() ? this.limit * 10 : this.limit, // there are 10 categories
            q: this.query(),
        };

        if (this.typeLimit()) {
            data.type_limit = this.limit;
        }

        this.req = http.get({url: this.searchUrl, data});
        const respData = await this.req;
        let results;

        const facets = [];
        let items = respData.facets;
        if (!(items instanceof Array)) {
            items = Object.keys(respData.facets);
        }
        for (const sortedFacet of this.sortOrder) {
            for (const facet of items) {
                if (sortedFacet === facet.type) {
                    facets.push(facet);
                }
            }
        }
        this.facets = facets;

        if (clearResults) {
            results = [];
        } else {
            results = this.results();
        }

        this.selectedIndex(-1);
        this.totalCount(respData.count);

        if (typeof this.totals[this.query()] === 'undefined') {
            this.totals[this.query()] = {};
        }

        for (const r of respData.results) {
            const result = new SearchResult(this);
            result.load(r);
            results.push(result);
        }

        this.showMore = respData.show_more;

        if (respData.next && !this.typeLimit()) {
            results.push(new MoreResults(this));
        }

        if (!respData.results.length) {
            results.push(new NoResults(this));
        }

        this.results(results);
        this.searching(false);
    }

    // Close the dropdown, but don't clear the text
    close() {
        if (this.isSearchResults()) {
            return;
        }
        this.searching(false);
        setTimeout(() => {
            this.results([]);
            this.selectedIndex(-1);
        });
    }

    keydown(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (!this.results().length) {
            return true;
        }
        if (key === 38 || key === 'Up' || key === 'ArrowUp') {
            this.select(this.selectedIndex() - 1);
            return;
        } else if (key === 40 || key === 'Down' || key === 'ArrowDown') {
            this.select(this.selectedIndex() + 1);
            return;
        } else if (key === 27 || key === 'Esc') {
            // Close dropdown on escape
            this.close();
        }
        return true;
    }

    select(index) {
        index = Math.max(Math.min(index, this.sortedResults().length - 1), 0);
        if (this.sortedResults()[index].selectable()) {
            this.selectedIndex(index);
        } else {
            const i = index + (index > this.selectedIndex() ? 1 : -1);
            if (i >= 0 && i < this.sortedResults().length) {
                this.select(i);
            }
        }
    }

    action() {
        if (this.selected()) {
            this.selected().action();
            return;
        }
        return true;
    }
}

(async function() {
    await events.ready();
    setBackgroundColours();
    const mgr = new SearchManager(DESKTOP_TYPE_LIMIT);

    const search = document.getElementsByClassName('js-query')[0];
    function onFocus() {
        const offset =
            search.getBoundingClientRect().top +
            (document.body.scrollTop || document.documentElement.scrollTop);
        document.body.scrollTop = document.documentElement.scrollTop = offset;
    }

    if (matchMedia) {
        const mq = window.matchMedia('(min-width: 768px)');
        mq.addListener(widthChange);
        widthChange(mq);
    }

    function widthChange(mq) {
        if (mq.matches) {
            mgr.typeLimit(DESKTOP_TYPE_LIMIT);
            events.unlisten(search, 'focus', onFocus);
        } else {
            mgr.typeLimit(MOBILE_TYPE_LIMIT);
            events.listen(search, 'focus', onFocus);
        }
    }

    let existingQuery;

    const elems = Array.from(
        document.getElementsByClassName('js-autocomplete'),
    );
    for (const elem of elems) {
        ko.applyBindings(mgr, elem);
        mgr.elems.push(elem);
        const query = elem.getElementsByClassName('js-query')[0];
        if (query && query.value) {
            existingQuery = query.value;
        }
    }

    if (existingQuery) {
        mgr.query(existingQuery);
    }
})();
